import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import {
  CellEditingStartedEvent,
  CellValueChangedEvent,
  RowSelectedEvent,
} from 'ag-grid-community';
import { AgGridReact, CustomDetailCellRendererProps } from 'ag-grid-react';
import { formatISO } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { useSearchParams } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { RowIdParams } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { PrimaryContext } from '@/services';
import {
  ODSemenItem,
  PulledSemenItem,
  ScheduledDonorItem,
  ScheduledSemenItem,
} from '@/services/semen-pull';
import { SiteOption } from '@/services/sites';
import { useContextMenu } from '@/utils/context-menu';

import {
  usePulledSemenQuery,
  useScheduledSemenQuery,
  useUnfinishedScheduledDonorsQuery,
  useUpdateDSSuggested,
} from '../../services/semen-pull/hooks';
import { usePulledSemenColDefs, useScheduledSemenColDefs } from './columnDefs';
import { defaultSemenPullColDef } from './defaults';
import { DonorsScheduledPulledGrid } from './grid-cell-rendered-components/DonorsScheduledPulledGrid';
import { DonorsScheduledScheduledSemenGrid } from './grid-cell-rendered-components/DonorsScheduledScheduledSemenGrid';
import { SemenPullActions } from './SemenPullActions';
import SemenPullContext from './SemenPullContext';
import { SemenPullSelector } from './SemenPullSelector';
import { ExtraSemenLabel, SemenState } from './types';

type ScheduledSemenWithExtraLabels = ScheduledSemenItem & {
  extraLabels?: string | null;
};

export const SemenPullPage = () => {
  const defaultGridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems: scheduledSemenContextMenu } = useContextMenu({
    idKey: 'semenId',
  });
  const { getContextMenuItems: pulledSemenContextMenu } = useContextMenu({
    idKey: 'semenId',
  });
  const pulledGridRef = useRef(null);
  const scheduledGridRef = useRef(null);
  const [params] = useSearchParams();
  const fertilizationSiteId = params.get('site');
  const selectedDate = params.get('date');
  const [sites, setSites] = useState<SiteOption[]>();
  const [ODselectedSemen, setODselectedSemen] = useState<ODSemenItem>();
  const [semenState, setSemenState] = useState<SemenState>({
    pullable: false,
    returnable: false,
    rejectable: false,
  });
  const [extraSemenLabels, setExtraSemenLabels] = useState<ExtraSemenLabel[]>(
    [],
  );
  const [currentScheduledSemen, setCurrentScheduledSemen] = useState<
    ScheduledSemenItem | undefined
  >();
  const [currentPulledSemen, setCurrentPulledSemen] = useState<
    PulledSemenItem | undefined
  >();

  // Derive selectedSite from URL params — no external setter needed
  const selectedSite = useMemo(() => {
    if (sites && fertilizationSiteId) {
      return (
        (sites.find((site) => site.id === fertilizationSiteId) as SiteOption) ??
        undefined
      );
    }
    return undefined;
  }, [fertilizationSiteId, sites]);

  const queryParams = {
    date: selectedDate,
    fertilizationSiteId: selectedSite?.id ?? null,
  };

  const scheduledSemenQuery = useScheduledSemenQuery(queryParams);
  const pulledSemenQuery = usePulledSemenQuery(queryParams);
  const unfinishedDonorsQuery = useUnfinishedScheduledDonorsQuery({
    date: selectedDate,
    siteId: selectedSite?.id ?? null,
  });
  const updateDSSuggested = useUpdateDSSuggested();

  const scheduledSemen = useMemo(
    () => cloneDeep(scheduledSemenQuery.data),
    [scheduledSemenQuery.data],
  );

  const pulledSemen = useMemo(
    () => cloneDeep(pulledSemenQuery.data),
    [pulledSemenQuery.data],
  );

  const { sireDuplicates, semenDuplicates } = useMemo(() => {
    const newSireDuplicates = new Set();
    const sireIdSet = new Set();
    const newSemenDuplicates = new Set();
    const semenIdSet = new Set();

    scheduledSemen?.forEach((row) => {
      if (sireIdSet.has(row.sireId)) newSireDuplicates.add(row.sireId);
      else sireIdSet.add(row.sireId);

      if (semenIdSet.has(row.semenId)) newSemenDuplicates.add(row.semenId);
      else semenIdSet.add(row.semenId);
    });

    return {
      sireDuplicates: newSireDuplicates,
      semenDuplicates: newSemenDuplicates,
    };
  }, [scheduledSemen]);

  // Duplicate detection for pulled semen grid highlighting
  const name_TypeDuplicates = useMemo(() => {
    const duplicates = new Set();
    const seen = new Set();

    pulledSemen?.forEach((row) => {
      if (seen.has(row.name_Type)) duplicates.add(row.name_Type);
      else seen.add(row.name_Type);
    });

    return duplicates;
  }, [pulledSemen]);

  const filterKey = `${selectedDate ?? ''}-${selectedSite?.id ?? ''}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    if (selectedDate && selectedSite) {
      setExtraSemenLabels([]);
    }
  }

  const value = useMemo(
    () => ({
      sites,
      setSites,
      selectedSite,
      ODselectedSemen: ODselectedSemen,
      setODselectedSemen: setODselectedSemen,
      semenState,
      setSemenState,
      scheduledSemen,
      pulledSemen,
      extraSemenLabels,
      setExtraSemenLabels,
      currentScheduledSemen,
      setCurrentScheduledSemen,
      currentPulledSemen,
      refetchScheduledSemen: scheduledSemenQuery.refetch,
      refetchPulledSemen: pulledSemenQuery.refetch,
    }),
    [
      sites,
      selectedSite,
      ODselectedSemen,
      semenState,
      scheduledSemen,
      pulledSemen,
      extraSemenLabels,
      currentScheduledSemen,
      currentPulledSemen,
      scheduledSemenQuery.refetch,
      pulledSemenQuery.refetch,
    ],
  );

  const handlePulledRowSelected = (e: RowSelectedEvent) => {
    setCurrentPulledSemen(e.data);
  };

  const handleScheduledRowSelected = (e: RowSelectedEvent) => {
    setCurrentScheduledSemen(e.data);
  };

  const handleScheduledEdit = useCallback(
    async (e: CellValueChangedEvent) => {
      if (e.colDef.field === 'moveNum') {
        if (
          (e?.data?.moveNum as number) > (e?.data?.quantity as number) &&
          e?.data?.moveNum !== 0
        ) {
          setSemenState((prevState) => ({ ...prevState, pullable: false }));
        } else {
          setSemenState((prevState) => ({ ...prevState, pullable: true }));
        }
      } else if (e.colDef.field === 'suggestedToPull') {
        await updateDSSuggested.mutateAsync({
          field: 'suggestedSemenMove',
          value: e?.data.suggestedToPull.toString(),
          donorScheduleDate: formatISO(new Date(selectedDate as string), {
            representation: 'date',
          }),
          fertSiteId: selectedSite?.id as string,
          semenId: e.data.semenId,
          semenType: e.data.semenTypeName,
          sireId: e.data.sireId,
          accountId: e.data.accountId,
          dsSemenLocId: e.data.semenStorageId,
        });
      } else if (e.colDef.field === 'extraLabels') {
        const rowData = e.data as ScheduledSemenWithExtraLabels;
        const semenId = rowData.semenId;
        const sireId = rowData.sireId ?? null;
        const semenType = rowData.semenType ?? null;
        const accountId = rowData.semenOwnerId ?? null;
        const parsedExtraLabels = rowData.extraLabels
          ? Number.parseInt(rowData.extraLabels, 10)
          : 1;

        setExtraSemenLabels((prev) => {
          const existingRows = prev.filter(
            (row) =>
              row.semenId !== semenId ||
              row.sireId !== sireId ||
              row.semenType !== semenType ||
              row.accountId !== accountId,
          );

          if (
            !(sireId || semenId) ||
            semenType == null ||
            !accountId ||
            Number.isNaN(parsedExtraLabels) ||
            parsedExtraLabels < 1
          ) {
            return existingRows;
          }

          return [
            ...existingRows,
            {
              sireId,
              semenId,
              semenType,
              accountId,
              extraLabels: parsedExtraLabels,
            },
          ];
        });
      }
    },
    [updateDSSuggested, selectedDate, selectedSite?.id, setExtraSemenLabels],
  );

  const handlePulledEdit = (e: CellValueChangedEvent) => {
    if (e.colDef.field === 'returnNum') {
      const rowData = e.data as PulledSemenItem;
      let moveable = true;
      let numRowsReturned = 0;
      if (
        (rowData.returnNum as number) > (rowData.pulledQuantity as number) &&
        rowData.returnNum !== 0
      ) {
        moveable = false;
      }
      pulledSemen?.forEach((row) => {
        const returnNum = row.returnNum as number;
        if (row.returnNum != null) {
          if (returnNum.toString() === '') {
            return;
          }
          if (returnNum <= (row.pulledQuantity as number)) {
            numRowsReturned = numRowsReturned + 1;
          }
        }
      });

      if (moveable && numRowsReturned >= 1) {
        setSemenState((prevState) => ({ ...prevState, returnable: true }));
      } else {
        setSemenState((prevState) => ({ ...prevState, returnable: false }));
      }

      if (numRowsReturned === 1) {
        setSemenState((prevState) => ({ ...prevState, rejectable: true }));
      } else {
        setSemenState((prevState) => ({ ...prevState, rejectable: false }));
      }
    }
  };

  const handleCellEditingStarted = (event: CellEditingStartedEvent) => {
    if (event.colDef.field === 'moveNum') {
      event.node.setSelected(true);
    }
  };

  const enableMultipleSemenTypesOneDay = useFeatureFlag(
    'Multiple_Semen_Types_One_Day',
  );

  const scheduledSemenColDefs = useScheduledSemenColDefs(selectedSite?.id);
  const pulledSemenColDefs = usePulledSemenColDefs();

  const getScheduledRowId = useCallback(
    ({ data }: RowIdParams<ScheduledSemenItem>) => {
      return enableMultipleSemenTypesOneDay
        ? `${data.semenId}-${data.sireId}-${data.semenStorageId}-${data.semenTypeName}-${data.donorScheduleId}-${data.colCode}`
        : `${data.semenId}-${data.sireId}-${data.semenStorageId}`;
    },
    [enableMultipleSemenTypesOneDay],
  );

  const getPulledRowId = useCallback(
    ({ data }: RowIdParams<PulledSemenItem>) =>
      `${data.semenId}-${data.semenTypeName}`,
    [],
  );

  return (
    <div className="semen-pull-page flex h-full flex-col">
      <SemenPullContext.Provider value={value}>
        <div className="mb-4 flex justify-between">
          <SemenPullSelector />
          <SemenPullActions scheduledGridRef={scheduledGridRef} />
        </div>
        <Splitter className="flex-1 overflow-hidden">
          <SplitterPanel size={57} className="flex h-full flex-col">
            <WithLocalStorage storageKey="semen-pull-scheduled-semen-grid">
              <div className="h-full w-full">
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full w-full`}
                  defaultColDef={defaultSemenPullColDef}
                  rowData={scheduledSemen}
                  columnDefs={scheduledSemenColDefs}
                  overlayLoadingTemplate="Please Select a Date and a Fertilization Site"
                  rowSelection="single"
                  onCellValueChanged={handleScheduledEdit}
                  onCellEditingStarted={handleCellEditingStarted}
                  onRowSelected={handleScheduledRowSelected}
                  masterDetail={true}
                  ref={scheduledGridRef}
                  getRowId={getScheduledRowId}
                  context={{
                    selectedSite: selectedSite as SiteOption,
                    selectedSemen: ODselectedSemen as ODSemenItem,
                    sireDuplicates: sireDuplicates,
                    semenDuplicates: semenDuplicates,
                    unfinishedScheduledDonors:
                      unfinishedDonorsQuery.data as ScheduledDonorItem[],
                  }}
                  getRowStyle={({ data, node }) => {
                    if (node.isSelected()) {
                      return { backgroundColor: 'var(--tog-yellow)' };
                    }
                    if (data?.transactionToday === 'Transfer') {
                      return { backgroundColor: 'lightblue' };
                    }
                    return undefined;
                  }}
                  detailCellRenderer={(
                    params: CustomDetailCellRendererProps,
                  ) => (
                    <DonorsScheduledScheduledSemenGrid
                      rowData={{ ...params.data }}
                    />
                  )}
                  getContextMenuItems={scheduledSemenContextMenu}
                />
              </div>
            </WithLocalStorage>
          </SplitterPanel>
          <SplitterPanel size={43} className="flex h-full flex-col">
            <WithLocalStorage storageKey="semen-pull-pulled-semen-grid">
              <div className="h-full w-full">
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full w-full`}
                  defaultColDef={defaultSemenPullColDef}
                  rowData={pulledSemen}
                  columnDefs={pulledSemenColDefs}
                  overlayLoadingTemplate="Please Select a Date and a Fertilization Site"
                  onRowSelected={handlePulledRowSelected}
                  rowSelection="single"
                  getRowId={getPulledRowId}
                  getRowStyle={({ data }) => {
                    if (data?.donors_Sire === 0) {
                      return { backgroundColor: 'lightgreen' };
                    }
                    if (data?.transactionToday === 'Transfer') {
                      return { backgroundColor: 'lightblue' };
                    }
                    return undefined;
                  }}
                  ref={pulledGridRef}
                  masterDetail={true}
                  context={{ name_TypeDuplicates: name_TypeDuplicates }}
                  detailCellRenderer={(
                    params: CustomDetailCellRendererProps,
                  ) => (
                    <DonorsScheduledPulledGrid rowData={{ ...params.data }} />
                  )}
                  onCellValueChanged={handlePulledEdit}
                  getContextMenuItems={pulledSemenContextMenu}
                />
              </div>
            </WithLocalStorage>
          </SplitterPanel>
        </Splitter>
      </SemenPullContext.Provider>
    </div>
  );
};
